import { useState } from 'react';
import type { JSX } from 'react';

/**
 * „Exportovat pro Facebook“ (D-321): nejdřív náhled počtů (bez adres), potom stažení CSV se zahashovanými údaji.
 * Jen administrátor (server to hlídá). Souhlas s předáním údajů Meta posuzuje provozovatel.
 */
const csrf = (): string => /(?:^|;\s*)hub_csrf=([^;]+)/.exec(document.cookie)?.[1] ?? '';

interface Preview {
  rows: number;
  skipped: { noConsent: number; blocked: number; excluded: number; unlinked: number };
  purpose: string;
}

export function FacebookExport({ path, label }: { path: string; label: string }): JSX.Element {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const call = async (previewOnly: boolean): Promise<Response> =>
    fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', 'x-csrf-token': csrf() },
      body: JSON.stringify({ preview: previewOnly }),
    });
  const check = async (): Promise<void> => {
    setError(null);
    const res = await call(true);
    if (!res.ok) {
      setError(
        ((await res.json().catch(() => null)) as { message?: string } | null)?.message ??
          'Export se nepodařil.',
      );
      return;
    }
    setPreview((await res.json()) as Preview);
  };
  const download = async (): Promise<void> => {
    const res = await call(false);
    if (!res.ok) {
      setError(
        ((await res.json().catch(() => null)) as { message?: string } | null)?.message ??
          'Export se nepodařil.',
      );
      return;
    }
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download =
      /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ??
      'meta-audience.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <span>
      <button
        className="btn"
        onClick={() => void check()}
        title="Spočítá, kolik kontaktů půjde do souboru (bez adres)"
      >
        {label}
      </button>
      {preview ? (
        <span className="note" role="status">
          {' '}
          {preview.rows} řádků (souhlas „{preview.purpose}“); vynecháno: bez souhlasu{' '}
          {preview.skipped.noConsent}, odhlášení nebo blokace {preview.skipped.blocked}, vylučovací
          seznamy {preview.skipped.excluded}
          {preview.skipped.unlinked ? `, bez kontaktu v hubu ${preview.skipped.unlinked}` : ''}.
          Soubor obsahuje jen zahashované údaje; souhlas s předáním Meta posuzujete vy.{' '}
          <button className="btn pri" disabled={preview.rows === 0} onClick={() => void download()}>
            Stáhnout CSV
          </button>
        </span>
      ) : null}
      {error ? <span className="note"> {error}</span> : null}
    </span>
  );
}
